import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type SubscriptionTier = 'free' | 'premium';

interface SubscriptionContextType {
  tier: SubscriptionTier;
  upgradeToPremium: () => void;
  downgradeToFree: () => void;
  isPremiumFeature: (featureName: PremiumFeature) => boolean;
}

// Premium features available in the paid version
export type PremiumFeature = 
  | 'ai-search'          // AI search to find foundations matching user descriptions
  | 'application-timing' // Tool for finding application periods
  | 'contact-info'       // Find websites and email addresses
  | 'contact-export';    // Extract names and phone numbers for easy contact

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Key for localStorage
const STORAGE_KEY = 'subscriptionTier';

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [tier, setTier] = useState<SubscriptionTier>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved as SubscriptionTier) || 'premium'; // Default to premium for testing
  });
  
  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, tier);
  }, [tier]);

  const upgradeToPremium = () => {
    setTier('premium');
  };

  const downgradeToFree = () => {
    setTier('free');
  };

  // Check if a feature is available for the current subscription tier
  const isPremiumFeature = (featureName: PremiumFeature): boolean => {
    if (tier === 'premium') return true;
    return false; // All listed features are premium-only
  };

  return (
    <SubscriptionContext.Provider 
      value={{
        tier,
        upgradeToPremium,
        downgradeToFree,
        isPremiumFeature
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
